import {ComponentProps, useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';

import {Divider, Grid, Typography} from '@mui/material';

import {AddAdminModal} from './AddAdminModal';
import {CompanyAdminRow} from './CompanyAdminRow';
import {BasicTable, TableColumn} from '../../../../../../components/BasicTable/BasicTable';
import {createBasicTableColumn} from '../../../../../../components/BasicTable/createBasicTableColumn';
import {EmpionButton} from '../../../../../../components/EmpionButton/EmpionButton';
import EmpionDialog from '../../../../../../components/EmpionDialog/EmpionDialog';
import {CompanyContext} from '../../../../../../contexts/CompanyContext/CompanyContext';
import {SnackBarContext} from '../../../../../../contexts/SnackBarContext/SnackBarContext';
import {UserContext} from '../../../../../../contexts/UserContext/UserContext';
import {CompanyAdmin} from '../../../../../../features/company/profile/types';
import {greenColors} from '../../../../../../layouts/theme/designSystem';
import {CompanyAdminRoleData} from '../../../../../../types/User';

export const CompanyAdmins = () => {
  const {currentUser} = useContext(UserContext);
  const companyId = (currentUser?.role.data as CompanyAdminRoleData)?.companyId;
  const [open, setOpen] = useState<boolean>(false);
  const {companyAdmins} = useContext(CompanyContext);

  const {dispatchMessage} = useContext(SnackBarContext);
  const {t} = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (location.state && location.state.inviteTeam) {
      setOpen(true);
    }
  }, [location.state]);

  useEffect(() => {
    if (companyAdmins.error) {
      dispatchMessage(t('shared.fetchingData.error'), 'error');
    }
  }, [companyAdmins.error, dispatchMessage, t]);

  const toggleModal = () => {
    setOpen(!open);
  };

  const handleCreate = () => {
    companyAdmins.refetch();
    toggleModal();
  };

  const createColumns = useCallback((): TableColumn[] => {
    return [
      createBasicTableColumn('name', t('companyAdmins.name'), {}),
      createBasicTableColumn('email', t('companyAdmins.email'), {}),
    ];
  }, [t]);

  const rowData = useMemo(
    () =>
      companyAdmins.data?.companyAdmins.map((admin: CompanyAdmin) => ({
        ...admin,
        id: admin.resourceId,
      })),
    [companyAdmins.data?.companyAdmins]
  );

  return (
    <>
      <Grid container>
        <Grid item container xs={12} justifyContent={'space-between'} alignItems={'flex-end'} mb={2}>
          <Grid item>
            <Typography variant='h3'>{t('companySettings.companyUsersTable.title')}</Typography>
            <Typography variant='body1'>{t('companySettings.companyUsersTable.subtitle')}</Typography>
          </Grid>
          <Grid item>
            <EmpionButton
              hoverBackgroundColor={greenColors[100]}
              sx={{
                backgroundColor: greenColors[700],
              }}
              onClick={() => setOpen(true)}
            >
              {t('companySettings.companyUsersTable.addUsers')}
            </EmpionButton>
          </Grid>
        </Grid>
        <Divider
          sx={{
            width: '100%',
            marginBottom: '16px',
          }}
        />
        <Grid item xs={12}>
          <BasicTable<ComponentProps<typeof CompanyAdminRow>>
            columns={createColumns()}
            Row={CompanyAdminRow}
            rowData={rowData || []}
          />
        </Grid>
      </Grid>
      <EmpionDialog
        showCloseButton
        sxTitle={{
          fontSize: '28px',
          fontWeight: 700,
          lineHeight: '42px',
        }}
        sx={{
          '& .MuiDialog-container': {
            '& .MuiPaper-root': {
              width: '100%',
              maxWidth: '736px',
              padding: '32px',
              borderRadius: '16px',
            },
          },
        }}
        open={open}
        onClose={() => setOpen(false)}
      >
        <AddAdminModal companyId={companyId} onCreate={handleCreate} />
      </EmpionDialog>
    </>
  );
};
